import { useCallback, useEffect } from 'react'
import useSWR from 'swr'
import { audienceImportSessionsApi } from '@/lib/api'
import type { AudienceImportStatusProjection } from '@/types/components/audiences/import-session'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import {
  useOptionalImportWizardRealtimeContext,
  useOptionalImportWizardStableContext,
} from '../context/ImportWizardContext'
import { useImportSessionWebsocket } from './useImportSessionWebsocket'
import { getAudienceImportStatusProjectionKey } from '../lib/cache-keys'
import { shouldRevalidateStatusProjectionForImportSessionUpdate } from '../lib/import-session-update-events'
import { incrementImportSessionClientMetric } from '../lib/import-session-client-metrics'
import { useRealtimeRepair } from '@/lib/realtime/useRealtimeRepair'
import { incrementPollWonRepair } from '@/lib/realtime/pollWonMetrics'
import { legacyRefreshInterval } from '@/lib/realtime/realtimeKillSwitch'
import {
  getRouteAckFromImportSessionEvent,
  mergeRouteAckIntoStatusProjection,
} from '../lib/import-session-deltas'

interface UseStatusProjectionOptions {
  lastMutationRequestId?: string | null
}

function shouldPoll(projection: AudienceImportStatusProjection | undefined) {
  const wizardPhase = projection?.flowSnapshot.wizardPhase
  const createState = projection?.flowSnapshot.branchSummaries.create.state
  const previewState = projection?.flowSnapshot.branchSummaries.preview.state

  return (
    projection?.flowSnapshot.machineState === 'creating' ||
    wizardPhase === 'analyze_running' ||
    wizardPhase === 'preview_hydrating' ||
    wizardPhase === 'preview_preparing' ||
    wizardPhase === 'create_running' ||
    createState === 'queued' ||
    createState === 'creating' ||
    createState === 'finalizing' ||
    previewState === 'queued' ||
    previewState === 'running'
  )
}

export function useStatusProjection(
  sessionId: string,
  options: UseStatusProjectionOptions = {},
) {
  const stableCtx = useOptionalImportWizardStableContext()
  const realtimeCtx = useOptionalImportWizardRealtimeContext()
  const key = getAudienceImportStatusProjectionKey(sessionId)
  const query = useSWR<AudienceImportStatusProjection | null>(
    key,
    sessionId
      ? () => audienceImportSessionsApi.getProgress(sessionId)
      : null,
    {
      refreshInterval: legacyRefreshInterval('IMPORT', 5000),
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    },
  )

  const revalidateProjection = useCallback(async () => {
    if (!sessionId) {
      return
    }
    await query.mutate(
      async () => audienceImportSessionsApi.getProgress(sessionId),
      {
        revalidate: false,
        populateCache: true,
      },
    )
  }, [query, sessionId])

  useRealtimeRepair({
    active: shouldPoll(query.data ?? undefined),
    onRepair: (reason) => {
      incrementPollWonRepair('IMPORT', reason)
      void revalidateProjection()
    },
  })

  const applyEventDelta = useCallback(
    async (event: ImportSessionUpdatedEvent) => {
      const ack = getRouteAckFromImportSessionEvent(event)
      if (!ack) {
        return false
      }
      await query.mutate(
        (current) => mergeRouteAckIntoStatusProjection(current, ack) ?? current,
        {
          revalidate: false,
        },
      )
      return true
    },
    [query],
  )

  const handleEvent = useCallback(
    (event: ImportSessionUpdatedEvent) => {
      if (!shouldRevalidateStatusProjectionForImportSessionUpdate(event)) {
        incrementImportSessionClientMetric('status_projection_revalidate_skipped', {
          sessionId,
          reason: event.reason,
        })
        return
      }
      if (event.routeAck) {
        void applyEventDelta(event)
        return
      }
      void revalidateProjection()
    },
    [applyEventDelta, revalidateProjection, sessionId],
  )

  const sessionIdForWebsocket = stableCtx ? '' : sessionId

  useEffect(() => {
    if (!stableCtx) return
    return stableCtx.registerImportSessionEventHandler(handleEvent)
  }, [handleEvent, stableCtx])

  const { isConnected } = useImportSessionWebsocket(
    sessionIdForWebsocket,
    handleEvent,
    {
      latestMachineVersion: query.data?.machineVersion,
      lastMutationRequestId: options.lastMutationRequestId,
    },
  )

  const isRealtimeConnected = stableCtx
    ? realtimeCtx?.isRealtimeConnected ?? false
    : isConnected

  return {
    projection: query.data ?? null,
    error: query.error as Error | undefined,
    isLoading: query.isLoading,
    isValidating: query.isValidating,
    isRealtimeConnected,
    mutate: query.mutate,
  }
}
